import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Share2, Heart, Star, MapPin, Clock, CheckCircle, PawPrint, Image, ChevronRight, Download } from 'lucide-react';
import { eventsApi } from '../services/eventsApi';
import FoundersVerifiedBadge from '../components/FoundersVerifiedBadge';
import PhotoGalleryModal from '../components/PhotoGalleryModal';
import GallerySlideMedia from '../components/GallerySlideMedia';
import { isGalleryImageUrl } from '../utils/galleryMedia';
import { getVisibleDotIndices } from '../utils/carouselDots';
import { shareListing } from '../utils/wishlist';
import { wishlistAPI } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';
import { TicketSelectionPanel } from '../components/TicketSelectionPanel';
import { isEventBookingEnabled, eventTicketsCtaLabel, isContactToBookEvent, isEventCtaInteractive, navigateEventContactBooking, getRazorpayEmbedConfig, openRazorpayBooking } from '../utils/eventBookingConfig';

const EventDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [currentImage, setCurrentImage] = useState(0);
  const [showGallery, setShowGallery] = useState(false);
  const [expandedFAQ, setExpandedFAQ] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [event, setEvent] = useState(null);
  const [saved, setSaved] = useState(false);
  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  const { isAuthenticated } = useAuth();
  const eventBookingEnabled = isEventBookingEnabled();
  const contactToBook = isContactToBookEvent(event);
  const razorpayEmbed = getRazorpayEmbedConfig(event);
  const ctaInteractive = isEventCtaInteractive(event);

  useBodyScrollLock(ticketModalOpen && eventBookingEnabled);

  const closeTicketModal = useCallback(() => setTicketModalOpen(false), []);

  useEffect(() => {
    if (!ticketModalOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setTicketModalOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [ticketModalOpen]);

  // Fetch event data when component mounts
  useEffect(() => {
    const fetchEvent = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const data = await eventsApi.getEventById(id);
        console.log('Event API Response:', data);
        
        // Extract the event object from the response
        const eventData = data.event || data;
        console.log('Event Data:', eventData);
        
        // Format the event data for display
        const formattedEvent = {
          id: eventData.eventId || id,
          eventId: eventData.eventId || id,
          title: eventData.title || 'Event Title',
          description: eventData.description || 'Event description not available.',
          tags: Array.isArray(eventData.tags) ? eventData.tags : [],
          bookingMode: eventData.bookingMode || null,
          razorpayEmbedUrl: eventData.razorpayEmbedUrl || null,
          razorpayEmbed: eventData.razorpayEmbed || null,
          razorpayPageUrl: eventData.razorpayPageUrl || null,
          rating: eventData.rating || 0,
          ratingText: eventData.ratingTag || 'Not Rated',
          founderVerified: eventData.founderVerified || false,
          category: eventData.category || 'general',
          date: formatEventDate(eventData.startDate, eventData.endDate),
          time: formatEventTime(eventData.startDate),
          location: eventData.city || 'Location',
          fullAddress: eventData.address || 'Address not available',
          venue: eventData.venue || 'Venue not specified',
          duration: eventData.duration || 120, // in minutes
          latitude: eventData.latitude || null,
          longitude: eventData.longitude || null,
          address: eventData.address || null,
          city: eventData.city || null,
          state: eventData.state || null,
          images: eventData.images || [
            'https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=800',
            'https://images.unsplash.com/photo-1583511655857-d19b40a7a54e?w=800',
            'https://images.unsplash.com/photo-1548199973-03cce0bbc87b?w=800'
          ],
          faqs: eventData.faqs || [
            {
              question: 'What should I bring to the event?',
              answer: 'Please bring your pet\'s vaccination records, a leash, and any comfort items your pet might need.'
            },
            {
              question: 'Is this event suitable for all pets?',
              answer: 'This event is designed for well-socialized pets. Please ensure your pet is comfortable around other animals and people.'
            }
          ],
          features: eventData.features || [],
          pricing: eventData.pricing || { basePrice: 0, taxes: 0, currency: 'INR' },
          capacity: eventData.capacity || { totalSeats: 100, maxPetsPerPerson: 2 },
          shows: eventData.shows || []
        };
        
        setEvent(formattedEvent);
      } catch (err) {
        console.error('Error fetching event:', err);
        setError(err.message || 'Failed to load event details');
        
        // Fallback to mock data
        setEvent({
          id: id || '1',
          title: 'The Great Mumbai Pet Show',
          description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
          rating: 8.7,
          ratingText: 'Excellent',
          founderVerified: true,
          date: 'Wed, 29 Oct',
          time: '6:30 PM onwards',
          location: 'Mumbai',
          fullAddress: '23, Pundole Building, Grant Road (W), Mumbai 400007',
          images: [
            'https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=800',
            'https://images.unsplash.com/photo-1583511655857-d19b40a7a54e?w=800',
            'https://images.unsplash.com/photo-1548199973-03cce0bbc87b?w=800'
          ],
          faqs: [
            {
              question: 'Is it safe for dogs to be on a boat?',
              answer: 'Yes, it is completely safe for dogs to be on our specially designed pet-friendly boats.'
            }
          ]
        });
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchEvent();
    }
  }, [id]);

  useEffect(() => {
    const checkSavedStatus = async () => {
      if (!id || !isAuthenticated) {
        setSaved(false);
        return;
      }

      try {
        const response = await wishlistAPI.getWishlist();
        const isSaved = (response.items || []).some((item) => item.type === 'event' && item.id === String(id));
        setSaved(isSaved);
      } catch (error) {
        console.error('Failed to fetch wishlist:', error);
        setSaved(false);
      }
    };

    checkSavedStatus();
  }, [id, isAuthenticated]);

  // Format event date for display
  const formatEventDate = (startDate, endDate) => {
    if (!startDate) return 'Date TBD';
    
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : start;
    
    const formatDate = (date) => {
      return date.toLocaleDateString('en-US', { 
        weekday: 'short',
        day: 'numeric',
        month: 'short'
      });
    };
    
    if (start.toDateString() === end.toDateString()) {
      return formatDate(start);
    } else {
      return `${formatDate(start)} - ${formatDate(end)}`;
    }
  };

  // Format event time for display
  const formatEventTime = (startDate) => {
    if (!startDate) return 'Time TBD';
    
    const date = new Date(startDate);
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }) + ' onwards';
  };

  // Touch handlers for mobile swipe
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && currentImage < event.images.length - 1) {
      // Swipe left - go to next image
      handleImageChange(currentImage + 1);
    } else if (isRightSwipe && currentImage > 0) {
      // Swipe right - go to previous image
      handleImageChange(currentImage - 1);
    }
  };

  const handleImageChange = (newIndex) => {
    if (isTransitioning) return;
    
    setIsTransitioning(true);
    setCurrentImage(newIndex);
    
    // Reset transition state after animation
    setTimeout(() => {
      setIsTransitioning(false);
    }, 300);
  };

  // Gallery modal functions
  const openGallery = () => setShowGallery(true);
  const closeGallery = () => setShowGallery(false);
  
  // FAQ toggle function
  const toggleFAQ = (index) => {
    setExpandedFAQ(expandedFAQ === index ? null : index);
  };

  const handleSaveEvent = async () => {
    if (!isAuthenticated) {
      alert('Please login to save favourites to your account.');
      navigate('/account');
      return;
    }

    if (!event) return;

    try {
      if (saved) {
        await wishlistAPI.removeFromWishlist('event', id);
        setSaved(false);
        return;
      }

      await wishlistAPI.addToWishlist({
        id,
        type: 'event',
        title: event.title,
        subtitle: event.location,
        image: event.images?.[0],
        path: `/event/${id}`
      });
      setSaved(true);
    } catch (error) {
      console.error('Failed to update wishlist:', error);
      alert('Could not update wishlist. Please try again.');
    }
  };

  const handleShareEvent = async () => {
    if (!event) return;
    const shared = await shareListing({ title: event.title, path: `/event/${id}` });
    if (shared) {
      alert('Link shared successfully.');
    } else {
      alert('Could not share right now. Please try again.');
    }
  };

  const eventGalleryPoster = useMemo(() => {
    if (!event?.images) return undefined;
    return event.images.find((u) => isGalleryImageUrl(u)) || undefined;
  }, [event?.images]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
      </div>
    );
  }

  // Error state
  if (error && !event) {
    return (
      <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">❌</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Event Not Found</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => navigate('/events')}
            className="bg-black text-white px-6 py-3 rounded-lg hover:bg-gray-800 transition-colors"
          >
            Back to Events
          </button>
        </div>
      </div>
    );
  }

  // No event data
  if (!event) {
    return (
      <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-500 text-6xl mb-4">📅</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Event Not Available</h2>
          <p className="text-gray-600 mb-4">This event is no longer available.</p>
          <button 
            onClick={() => navigate('/events')}
            className="bg-black text-white px-6 py-3 rounded-lg hover:bg-gray-800 transition-colors"
          >
            Back to Events
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF7F2] md:py-[60px] md:px-[60px] lg:px-[120px]">
      {/* Hero Section with Image Carousel */}
      <div className="relative">
        {/* Mobile: Full width image with carousel */}
        <div className="md:hidden relative aspect-square overflow-hidden">
          <div
            className="w-full h-full flex transition-transform duration-300 ease-out"
            style={{
              transform: `translateX(-${currentImage * 100}%)`,
            }}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            {event.images.map((image, index) => (
              <div key={index} className="w-full h-full flex-shrink-0">
                <GallerySlideMedia
                  src={image}
                  poster={eventGalleryPoster}
                  alt={`${event.title} ${index + 1}`}
                  active={index === currentImage}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>
          
          {/* Gradient overlays */}
          <div className='h-[46px] absolute bottom-0 left-0 w-full'
            style={{
              background: 'linear-gradient(178deg,rgb(121, 121, 121, 0) 0%,rgb(19, 19, 19, 0.33) 100%)',
              backdropFilter: 'blur(8px)',
              mask: 'linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, rgba(255, 255, 255) 100%)',
            }}
          ></div>
          <div className='h-[46px] absolute top-0 left-0 w-full'
            style={{
              background: 'linear-gradient(360deg,rgb(121, 121, 121, 0) 0%,rgb(19, 19, 19, 0.33) 100%)',
              backdropFilter: 'blur(8px)',
              mask: 'linear-gradient(0deg, rgba(0, 0, 0, 0.00) 0%, rgba(255, 255, 255) 100%)',
            }}
          ></div>
          
          {/* Back Button */}
          <Link to="/events" className="absolute top-4 left-4 z-10">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <g clipPath="url(#clip0_2846_7407)">
                <path d="M5 12.0001H19" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M5 12.0001L11 18.0001" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M5 12.0001L11 6.00012" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </g>
              <defs>
                <clipPath id="clip0_2846_7407">
                  <rect width="24" height="24" fill="white"/>
                </clipPath>
              </defs>
            </svg>
          </Link>

          
          <div className="absolute bottom-4 right-4 flex flex-col items-end gap-[16px]">
          {/* Gallery Icon - Mobile */}
          <button 
            onClick={() => setShowGallery(true)}
            className=" bg-black bg-opacity-60 rounded-full p-[6px] z-10 hover:bg-opacity-80 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
              <g clipPath="url(#clip0_250_25568)">
                <path d="M19 3H15C14.4696 3 13.9609 3.21071 13.5858 3.58579C13.2107 3.96086 13 4.46957 13 5V17C13 18.0609 13.4214 19.0783 14.1716 19.8284C14.9217 20.5786 15.9391 21 17 21C18.0609 21 19.0783 20.5786 19.8284 19.8284C20.5786 19.0783 21 18.0609 21 17V5C21 4.46957 20.7893 3.96086 20.4142 3.58579C20.0391 3.21071 19.5304 3 19 3Z" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M13.0004 7.34998L11.0004 5.34998C10.6253 4.97503 10.1167 4.7644 9.58636 4.7644C9.05603 4.7644 8.54742 4.97503 8.17236 5.34998L5.34436 8.17798C4.96942 8.55303 4.75879 9.06165 4.75879 9.59198C4.75879 10.1223 4.96942 10.6309 5.34436 11.006L14.3444 20.006" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M7.3 13H5C4.46957 13 3.96086 13.2107 3.58579 13.5858C3.21071 13.9609 3 14.4696 3 15V19C3 19.5304 3.21071 20.0391 3.58579 20.4142C3.96086 20.7893 4.46957 21 5 21H17" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </g>
              <defs>
                <clipPath id="clip0_250_25568">
                  <rect width="24" height="24" fill="white"/>
                </clipPath>
              </defs>
            </svg>
          </button>

          {/* Image Indicators */}
          <div className="flex space-x-[6px] items-center z-10">
            {getVisibleDotIndices(event.images.length, currentImage).map((index) => (
              <button
                key={index}
                type="button"
                aria-label={`Image ${index + 1}`}
                onClick={() => handleImageChange(index)}
                className={`bg-white rounded-full transition-all duration-300 ${
                  index === currentImage ? 'w-[14px] h-[14px]' : 'w-[6.5px] h-[6.5px]'
                }`}
              />
            ))}
          </div>
        </div>
        </div>

        {/* Desktop: Grid layout */}
        <div className="hidden md:block">
          <div className="flex flex-col">
            <div className="relative">
              <div className="grid grid-cols-3 gap-[20px]">
                <div className="col-span-2">
                  <div className="h-[50vh] overflow-hidden">
                    <GallerySlideMedia
                      src={event.images[currentImage]}
                      poster={eventGalleryPoster}
                      alt={event.title}
                      active
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                <div className="h-[50vh] flex flex-col gap-[20px]">
                  {event.images.slice(1, 3).map((image, index) => (
                    <div key={index} className="relative h-[calc(50%-10px)] overflow-hidden">
                      <GallerySlideMedia
                        src={image}
                        poster={eventGalleryPoster}
                        alt={`${event.title} ${index + 2}`}
                        active={false}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Gallery Icon */}
              <button 
                onClick={openGallery}
                className="absolute bottom-[16px] right-[16px] rounded-full p-[8px] bg-[#131313] bg-opacity-60 cursor-pointer transition-colors"
              >
                <Image className="w-6 h-6 text-white" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content Card */}
      <div className="flex flex-col md:flex-row items-start bg-[#FAF7F2] justify-between gap-[20px] px-[16px] py-[20px] md:px-0">
      
        
            {/* Event Header */}
            <div className="w-full">
            <div className="flex flex-col items-start justify-between">
                <h1 className="text-[20px] font-medium tracking-[-0.5px] mb-[20px] leading-none text-black flex items-center">
                {event.title} 
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                        <g clip-path="url(#clip0_2846_8553)">
                            <path d="M14.9994 3.87024C15.7951 3.87024 16.5581 4.18631 17.1207 4.74892C17.6833 5.31153 17.9994 6.07459 17.9994 6.87024C18.4927 6.87013 18.9785 6.99169 19.4136 7.22415C19.8488 7.45661 20.2199 7.7928 20.4941 8.20293C20.7682 8.61306 20.937 9.08448 20.9855 9.57543C21.034 10.0664 20.9607 10.5617 20.772 11.0175C20.5833 11.4734 20.2851 11.8757 19.9039 12.1887C19.5226 12.5018 19.07 12.716 18.5862 12.8124C18.1024 12.9088 17.6022 12.8844 17.1301 12.7413C16.6579 12.5983 16.2284 12.341 15.8794 11.9922L11.1214 16.7502C11.4648 17.0939 11.7196 17.5158 11.8639 17.9798C12.0081 18.4437 12.0376 18.9357 11.9497 19.4135C11.8618 19.8913 11.6592 20.3406 11.3592 20.7228C11.0593 21.105 10.671 21.4086 10.2278 21.6076C9.78457 21.8065 9.29966 21.8949 8.81475 21.865C8.32984 21.8351 7.85944 21.6879 7.44399 21.4361C7.02854 21.1842 6.68047 20.8352 6.42971 20.4191C6.17895 20.003 6.033 19.5322 6.00441 19.0472V18.8702H5.82841C5.34324 18.8418 4.87225 18.6959 4.45595 18.4452C4.03964 18.1944 3.69049 17.8462 3.43854 17.4307C3.18659 17.0151 3.03937 16.5445 3.00956 16.0594C2.97974 15.5743 3.06823 15.0893 3.26739 14.646C3.46656 14.2026 3.77044 13.8143 4.15291 13.5145C4.53537 13.2146 4.98495 13.0122 5.46299 12.9245C5.94102 12.8369 6.43318 12.8667 6.89714 13.0114C7.36109 13.1561 7.78295 13.4114 8.12641 13.7552L12.8844 8.99724C12.4647 8.5778 12.1788 8.04331 12.0629 7.46137C11.9469 6.87944 12.0062 6.27619 12.2331 5.72793C12.46 5.17966 12.8444 4.71101 13.3377 4.38123C13.831 4.05145 14.411 3.87537 15.0044 3.87524L14.9994 3.87024Z" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
                        </g>
                        <defs>
                            <clipPath id="clip0_2846_8553">
                            <rect width="24" height="24" fill="white" transform="translate(0 0.870239)"/>
                            </clipPath>
                        </defs>
                    </svg>
                </h1>
                <p className="text-[14px] font-normal tracking-[-0.5px] leading-none text-[#bebebe] ">{event.description}</p>
            </div>
            <div className='flex my-[20px] items-center justify-center'>
                    <svg width="100%" height="1px">
                      <line 
                        x1="0" y1="1" x2="100%" y2="1" 
                        stroke="#f0f0f0" 
                        strokeWidth="2" 
                        strokeDasharray="10 4"
                      />
                    </svg>
             </div>
            {/* Rating and Badges */}
          <div className="flex items-center space-x-3 mb-[20px]">
            <div className="flex items-center gap-[10px]">
              <span className="text-[14px] text-[#fff] bg-black rounded-[14px] w-[34px] h-[34px] flex items-center justify-center">{event.rating}</span> {event.ratingText}
            </div>
            {event.founderVerified && <FoundersVerifiedBadge />}
          </div>
            

          

            {/* Event Details Box */}
            <div className="rounded-[16px] border-[1px] border-[#DFD7CB] mb-[20px]">
                <div className="">
                    <div className="flex items-center gap-[12px] p-[20px]">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                        <g clip-path="url(#clip0_2846_8585)">
                            <path d="M9 11.8701C9 12.6658 9.31607 13.4288 9.87868 13.9914C10.4413 14.554 11.2044 14.8701 12 14.8701C12.7956 14.8701 13.5587 14.554 14.1213 13.9914C14.6839 13.4288 15 12.6658 15 11.8701C15 11.0745 14.6839 10.3114 14.1213 9.7488C13.5587 9.18619 12.7956 8.87012 12 8.87012C11.2044 8.87012 10.4413 9.18619 9.87868 9.7488C9.31607 10.3114 9 11.0745 9 11.8701Z" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                            <path d="M17.657 17.5271L13.414 21.7701C13.039 22.1447 12.5306 22.3551 12.0005 22.3551C11.4704 22.3551 10.962 22.1447 10.587 21.7701L6.343 17.5271C5.22422 16.4082 4.46234 14.9828 4.15369 13.4309C3.84504 11.8791 4.00349 10.2706 4.60901 8.8088C5.21452 7.34701 6.2399 6.09761 7.55548 5.21858C8.87107 4.33954 10.4178 3.87036 12 3.87036C13.5822 3.87036 15.1289 4.33954 16.4445 5.21858C17.7601 6.09761 18.7855 7.34701 19.391 8.8088C19.9965 10.2706 20.155 11.8791 19.8463 13.4309C19.5377 14.9828 18.7758 16.4082 17.657 17.5271Z" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        </g>
                        <defs>
                            <clipPath id="clip0_2846_8585">
                            <rect width="24" height="24" fill="white" transform="translate(0 0.870117)"/>
                            </clipPath>
                        </defs>
                        </svg>
                        <span className="text-[14px] font-medium text-[#131313]">{event.location}</span>
                    </div>
                </div>
            </div>





            <div className='flex my-[20px] items-center justify-center'>
                    <svg width="100%" height="1px">
                      <line 
                        x1="0" y1="1" x2="100%" y2="1" 
                        stroke="#f0f0f0" 
                        strokeWidth="2" 
                        strokeDasharray="10 4"
                      />
                    </svg>
             </div>
                {/* Actions */}
            <div className="flex items-center space-x-[20px] mb-4">
            <button onClick={handleSaveEvent} className="flex items-center text-[#007AFF] gap-[4px] font-medium">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                <g clip-path="url(#clip0_2846_8594)">
                    <path d="M19.4993 13.442L11.9993 20.87L4.49932 13.442C4.00463 12.9607 3.61497 12.3821 3.35487 11.7427C3.09478 11.1033 2.96989 10.417 2.98806 9.72698C3.00624 9.03697 3.16709 8.35819 3.46048 7.73339C3.75388 7.10859 4.17346 6.5513 4.69281 6.09663C5.21216 5.64196 5.82003 5.29974 6.47814 5.09153C7.13624 4.88332 7.83033 4.81363 8.51669 4.88685C9.20306 4.96007 9.86682 5.17461 10.4662 5.51696C11.0656 5.85931 11.5876 6.32205 11.9993 6.87605C12.4129 6.32607 12.9355 5.86737 13.5344 5.52865C14.1334 5.18994 14.7958 4.9785 15.4803 4.90757C16.1647 4.83664 16.8564 4.90775 17.5121 5.11644C18.1678 5.32514 18.7734 5.66692 19.2909 6.12041C19.8084 6.5739 20.2268 7.12933 20.5197 7.75194C20.8127 8.37455 20.9741 9.05093 20.9936 9.73876C21.0132 10.4266 20.8906 11.111 20.6335 11.7493C20.3763 12.3876 19.9902 12.9659 19.4993 13.448" stroke="#007AFF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </g>
                <defs>
                    <clipPath id="clip0_2846_8594">
                    <rect width="24" height="24" fill="white" transform="translate(0 0.870117)"/>
                    </clipPath>
                </defs>
                </svg>
                {saved ? 'Saved' : 'Save'}
            </button>
            <button onClick={handleShareEvent} className="flex items-center text-[#007AFF] gap-[4px] font-medium">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                <g clip-path="url(#clip0_2846_8600)">
                    <path d="M3 12.8701C3 13.6658 3.31607 14.4288 3.87868 14.9914C4.44129 15.554 5.20435 15.8701 6 15.8701C6.79565 15.8701 7.55871 15.554 8.12132 14.9914C8.68393 14.4288 9 13.6658 9 12.8701C9 12.0745 8.68393 11.3114 8.12132 10.7488C7.55871 10.1862 6.79565 9.87012 6 9.87012C5.20435 9.87012 4.44129 10.1862 3.87868 10.7488C3.31607 11.3114 3 12.0745 3 12.8701Z" stroke="#007AFF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M15 6.87012C15 7.66577 15.3161 8.42883 15.8787 8.99144C16.4413 9.55405 17.2044 9.87012 18 9.87012C18.7956 9.87012 19.5587 9.55405 20.1213 8.99144C20.6839 8.42883 21 7.66577 21 6.87012C21 6.07447 20.6839 5.31141 20.1213 4.7488C19.5587 4.18619 18.7956 3.87012 18 3.87012C17.2044 3.87012 16.4413 4.18619 15.8787 4.7488C15.3161 5.31141 15 6.07447 15 6.87012Z" stroke="#007AFF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M15 18.8701C15 19.6658 15.3161 20.4288 15.8787 20.9914C16.4413 21.554 17.2044 21.8701 18 21.8701C18.7956 21.8701 19.5587 21.554 20.1213 20.9914C20.6839 20.4288 21 19.6658 21 18.8701C21 18.0745 20.6839 17.3114 20.1213 16.7488C19.5587 16.1862 18.7956 15.8701 18 15.8701C17.2044 15.8701 16.4413 16.1862 15.8787 16.7488C15.3161 17.3114 15 18.0745 15 18.8701Z" stroke="#007AFF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M8.69922 11.5702L15.2992 8.17017" stroke="#007AFF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M8.69922 14.1702L15.2992 17.5702" stroke="#007AFF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </g>
                <defs>
                    <clipPath id="clip0_2846_8600">
                    <rect width="24" height="24" fill="white" transform="translate(0 0.870117)"/>
                    </clipPath>
                </defs>
                </svg>
                            Share
            </button>
            </div>

            </div>
    
    </div>

    <div className="flex flex-col gap-[20px] pb-28 md:pb-32">
        {/* About Section */}
        <div className="px-[16px] md:p-0">
            <div className="w-full p-[20px] bg-white rounded-[16px]">
                <h2 className="text-[18px] font-medium text-black mb-[20px] flex gap-[3px] items-center">
                About
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                    <g clip-path="url(#clip0_2840_7462)">
                        <path d="M12 11.8111C14.333 8.50312 12.167 3.98812 11 2.87012C11 6.26512 8.765 8.16912 7.333 9.57612C5.903 10.9841 5 13.1971 5 15.1641C5 18.8681 8.134 21.8701 12 21.8701C15.866 21.8701 19 18.8681 19 15.1641C19 13.4521 17.768 10.7611 16.667 9.57612C14.583 12.9291 13.41 12.9291 12 11.8111Z" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
                    </g>
                    <defs>
                        <clipPath id="clip0_2840_7462">
                        <rect width="24" height="24" fill="white" transform="translate(0 0.870117)"/>
                        </clipPath>
                    </defs>
                    </svg>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                    <g clip-path="url(#clip0_2840_7465)">
                        <path d="M15.0004 3.87012C15.796 3.87012 16.5591 4.18619 17.1217 4.7488C17.6843 5.31141 18.0004 6.07447 18.0004 6.87012C18.4937 6.87001 18.9795 6.99157 19.4146 7.22403C19.8498 7.45649 20.2209 7.79268 20.495 8.20281C20.7692 8.61294 20.938 9.08436 20.9865 9.57531C21.035 10.0663 20.9616 10.5616 20.773 11.0174C20.5843 11.4733 20.2861 11.8755 19.9049 12.1886C19.5236 12.5017 19.071 12.7159 18.5872 12.8123C18.1033 12.9087 17.6032 12.8843 17.1311 12.7412C16.6589 12.5981 16.2293 12.3408 15.8804 11.9921L11.1224 16.7501C11.4658 17.0938 11.7206 17.5157 11.8648 17.9796C12.0091 18.4435 12.0386 18.9356 11.9507 19.4134C11.8628 19.8912 11.6601 20.3405 11.3602 20.7227C11.0603 21.1049 10.672 21.4085 10.2288 21.6075C9.78554 21.8064 9.30063 21.8948 8.81572 21.8649C8.33081 21.835 7.86041 21.6878 7.44497 21.436C7.02952 21.1841 6.68145 20.8351 6.43069 20.419C6.17993 20.0029 6.03398 19.5321 6.00539 19.0471V18.8701H5.82939C5.34422 18.8417 4.87323 18.6958 4.45692 18.445C4.04062 18.1943 3.69147 17.8461 3.43952 17.4305C3.18756 17.0149 3.04035 16.5444 3.01053 16.0593C2.98072 15.5742 3.0692 15.0891 3.26837 14.6458C3.46753 14.2025 3.77142 13.8142 4.15388 13.5144C4.53634 13.2145 4.98593 13.0121 5.46396 12.9244C5.942 12.8368 6.43416 12.8666 6.89812 13.0113C7.36207 13.156 7.78393 13.4113 8.12739 13.7551L12.8854 8.99712C12.4657 8.57767 12.1798 8.04319 12.0638 7.46125C11.9479 6.87932 12.0072 6.27607 12.2341 5.72781C12.461 5.17954 12.8454 4.71089 13.3387 4.38111C13.832 4.05133 14.412 3.87524 15.0054 3.87512L15.0004 3.87012Z" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
                    </g>
                    <defs>
                        <clipPath id="clip0_2840_7465">
                        <rect width="24" height="24" fill="white" transform="translate(0 0.870117)"/>
                        </clipPath>
                    </defs>
                    </svg>
                </h2>
                <p className="text-[14px] text-[#8c8c8c] leading-[1.2]">
                        {event.description}
                </p>
                
                </div>
                
        </div>

        {/* Venue Section */}
        <div className="px-[16px] md:p-0">
            <div className="w-full p-[20px] bg-white rounded-[16px]">
                <h2 className="text-[18px] font-medium text-black mb-[20px] flex gap-[3px] items-center">
                Venue
                   
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                    <g clip-path="url(#clip0_2840_7465)">
                        <path d="M15.0004 3.87012C15.796 3.87012 16.5591 4.18619 17.1217 4.7488C17.6843 5.31141 18.0004 6.07447 18.0004 6.87012C18.4937 6.87001 18.9795 6.99157 19.4146 7.22403C19.8498 7.45649 20.2209 7.79268 20.495 8.20281C20.7692 8.61294 20.938 9.08436 20.9865 9.57531C21.035 10.0663 20.9616 10.5616 20.773 11.0174C20.5843 11.4733 20.2861 11.8755 19.9049 12.1886C19.5236 12.5017 19.071 12.7159 18.5872 12.8123C18.1033 12.9087 17.6032 12.8843 17.1311 12.7412C16.6589 12.5981 16.2293 12.3408 15.8804 11.9921L11.1224 16.7501C11.4658 17.0938 11.7206 17.5157 11.8648 17.9796C12.0091 18.4435 12.0386 18.9356 11.9507 19.4134C11.8628 19.8912 11.6601 20.3405 11.3602 20.7227C11.0603 21.1049 10.672 21.4085 10.2288 21.6075C9.78554 21.8064 9.30063 21.8948 8.81572 21.8649C8.33081 21.835 7.86041 21.6878 7.44497 21.436C7.02952 21.1841 6.68145 20.8351 6.43069 20.419C6.17993 20.0029 6.03398 19.5321 6.00539 19.0471V18.8701H5.82939C5.34422 18.8417 4.87323 18.6958 4.45692 18.445C4.04062 18.1943 3.69147 17.8461 3.43952 17.4305C3.18756 17.0149 3.04035 16.5444 3.01053 16.0593C2.98072 15.5742 3.0692 15.0891 3.26837 14.6458C3.46753 14.2025 3.77142 13.8142 4.15388 13.5144C4.53634 13.2145 4.98593 13.0121 5.46396 12.9244C5.942 12.8368 6.43416 12.8666 6.89812 13.0113C7.36207 13.156 7.78393 13.4113 8.12739 13.7551L12.8854 8.99712C12.4657 8.57767 12.1798 8.04319 12.0638 7.46125C11.9479 6.87932 12.0072 6.27607 12.2341 5.72781C12.461 5.17954 12.8454 4.71089 13.3387 4.38111C13.832 4.05133 14.412 3.87524 15.0054 3.87512L15.0004 3.87012Z" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
                    </g>
                    <defs>
                        <clipPath id="clip0_2840_7465">
                        <rect width="24" height="24" fill="white" transform="translate(0 0.870117)"/>
                        </clipPath>
                    </defs>
                    </svg>
                </h2>
                <div className="flex items-center mb-[20px] gap-[20px]">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                        <g clip-path="url(#clip0_2846_8630)">
                            <path d="M9 11.8701C9 12.6658 9.31607 13.4288 9.87868 13.9914C10.4413 14.554 11.2044 14.8701 12 14.8701C12.7956 14.8701 13.5587 14.554 14.1213 13.9914C14.6839 13.4288 15 12.6658 15 11.8701C15 11.0745 14.6839 10.3114 14.1213 9.7488C13.5587 9.18619 12.7956 8.87012 12 8.87012C11.2044 8.87012 10.4413 9.18619 9.87868 9.7488C9.31607 10.3114 9 11.0745 9 11.8701Z" stroke="#8C8C8C" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                            <path d="M17.657 17.5271L13.414 21.7701C13.039 22.1447 12.5306 22.3551 12.0005 22.3551C11.4704 22.3551 10.962 22.1447 10.587 21.7701L6.343 17.5271C5.22422 16.4082 4.46234 14.9828 4.15369 13.4309C3.84504 11.8791 4.00349 10.2706 4.60901 8.8088C5.21452 7.34701 6.2399 6.09761 7.55548 5.21858C8.87107 4.33954 10.4178 3.87036 12 3.87036C13.5822 3.87036 15.1289 4.33954 16.4445 5.21858C17.7601 6.09761 18.7855 7.34701 19.391 8.8088C19.9965 10.2706 20.155 11.8791 19.8463 13.4309C19.5377 14.9828 18.7758 16.4082 17.657 17.5271Z" stroke="#8C8C8C" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        </g>
                        <defs>
                            <clipPath id="clip0_2846_8630">
                            <rect width="24" height="24" fill="white" transform="translate(0 0.870117)"/>
                            </clipPath>
                        </defs>
                        </svg>
                    <span className="text-[14px] leading-[1.2] text-[#8c8c8c]">{event.fullAddress}</span>
                </div>
                <button className="text-[#007AFF] text-[14px] font-medium">
                    Get Directions
                </button>
                
            </div>
                
        </div>
        

        {/* Gallery Section */}
        <div className="px-[16px] md:p-0">
            <div className="w-full p-[20px] bg-white rounded-[16px]">
                <h2 className="text-[18px] font-medium text-black mb-[20px] flex gap-[3px] items-center">
                Gallery
                   
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                    <g clip-path="url(#clip0_2840_7465)">
                        <path d="M15.0004 3.87012C15.796 3.87012 16.5591 4.18619 17.1217 4.7488C17.6843 5.31141 18.0004 6.07447 18.0004 6.87012C18.4937 6.87001 18.9795 6.99157 19.4146 7.22403C19.8498 7.45649 20.2209 7.79268 20.495 8.20281C20.7692 8.61294 20.938 9.08436 20.9865 9.57531C21.035 10.0663 20.9616 10.5616 20.773 11.0174C20.5843 11.4733 20.2861 11.8755 19.9049 12.1886C19.5236 12.5017 19.071 12.7159 18.5872 12.8123C18.1033 12.9087 17.6032 12.8843 17.1311 12.7412C16.6589 12.5981 16.2293 12.3408 15.8804 11.9921L11.1224 16.7501C11.4658 17.0938 11.7206 17.5157 11.8648 17.9796C12.0091 18.4435 12.0386 18.9356 11.9507 19.4134C11.8628 19.8912 11.6601 20.3405 11.3602 20.7227C11.0603 21.1049 10.672 21.4085 10.2288 21.6075C9.78554 21.8064 9.30063 21.8948 8.81572 21.8649C8.33081 21.835 7.86041 21.6878 7.44497 21.436C7.02952 21.1841 6.68145 20.8351 6.43069 20.419C6.17993 20.0029 6.03398 19.5321 6.00539 19.0471V18.8701H5.82939C5.34422 18.8417 4.87323 18.6958 4.45692 18.445C4.04062 18.1943 3.69147 17.8461 3.43952 17.4305C3.18756 17.0149 3.04035 16.5444 3.01053 16.0593C2.98072 15.5742 3.0692 15.0891 3.26837 14.6458C3.46753 14.2025 3.77142 13.8142 4.15388 13.5144C4.53634 13.2145 4.98593 13.0121 5.46396 12.9244C5.942 12.8368 6.43416 12.8666 6.89812 13.0113C7.36207 13.156 7.78393 13.4113 8.12739 13.7551L12.8854 8.99712C12.4657 8.57767 12.1798 8.04319 12.0638 7.46125C11.9479 6.87932 12.0072 6.27607 12.2341 5.72781C12.461 5.17954 12.8454 4.71089 13.3387 4.38111C13.832 4.05133 14.412 3.87524 15.0054 3.87512L15.0004 3.87012Z" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
                    </g>
                    <defs>
                        <clipPath id="clip0_2840_7465">
                        <rect width="24" height="24" fill="white" transform="translate(0 0.870117)"/>
                        </clipPath>
                    </defs>
                    </svg>
                </h2>
                <div className="grid grid-cols-2 gap-[4px]">
                    {event.images.slice(0, 4).map((image, index) => (
                    <div key={index} className="aspect-square overflow-hidden ">
                        <img 
                        src={image} 
                        alt={`${event.title} ${index + 1}`}
                        className="w-full h-full object-cover"
                        />
                    </div>
                    ))}
                </div>
          </div>
          
        </div>

        {/* FAQs Section */}
        <div className="px-[16px] md:p-0">
            <div className="w-full p-[20px] pb-0 bg-white rounded-[16px]">
                <h2 className="text-[18px] font-medium text-black mb-[20px] flex gap-[3px] items-center">
                FAQs
                   
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                    <g clip-path="url(#clip0_2840_7465)">
                        <path d="M15.0004 3.87012C15.796 3.87012 16.5591 4.18619 17.1217 4.7488C17.6843 5.31141 18.0004 6.07447 18.0004 6.87012C18.4937 6.87001 18.9795 6.99157 19.4146 7.22403C19.8498 7.45649 20.2209 7.79268 20.495 8.20281C20.7692 8.61294 20.938 9.08436 20.9865 9.57531C21.035 10.0663 20.9616 10.5616 20.773 11.0174C20.5843 11.4733 20.2861 11.8755 19.9049 12.1886C19.5236 12.5017 19.071 12.7159 18.5872 12.8123C18.1033 12.9087 17.6032 12.8843 17.1311 12.7412C16.6589 12.5981 16.2293 12.3408 15.8804 11.9921L11.1224 16.7501C11.4658 17.0938 11.7206 17.5157 11.8648 17.9796C12.0091 18.4435 12.0386 18.9356 11.9507 19.4134C11.8628 19.8912 11.6601 20.3405 11.3602 20.7227C11.0603 21.1049 10.672 21.4085 10.2288 21.6075C9.78554 21.8064 9.30063 21.8948 8.81572 21.8649C8.33081 21.835 7.86041 21.6878 7.44497 21.436C7.02952 21.1841 6.68145 20.8351 6.43069 20.419C6.17993 20.0029 6.03398 19.5321 6.00539 19.0471V18.8701H5.82939C5.34422 18.8417 4.87323 18.6958 4.45692 18.445C4.04062 18.1943 3.69147 17.8461 3.43952 17.4305C3.18756 17.0149 3.04035 16.5444 3.01053 16.0593C2.98072 15.5742 3.0692 15.0891 3.26837 14.6458C3.46753 14.2025 3.77142 13.8142 4.15388 13.5144C4.53634 13.2145 4.98593 13.0121 5.46396 12.9244C5.942 12.8368 6.43416 12.8666 6.89812 13.0113C7.36207 13.156 7.78393 13.4113 8.12739 13.7551L12.8854 8.99712C12.4657 8.57767 12.1798 8.04319 12.0638 7.46125C11.9479 6.87932 12.0072 6.27607 12.2341 5.72781C12.461 5.17954 12.8454 4.71089 13.3387 4.38111C13.832 4.05133 14.412 3.87524 15.0054 3.87512L15.0004 3.87012Z" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
                    </g>
                    <defs>
                        <clipPath id="clip0_2840_7465">
                        <rect width="24" height="24" fill="white" transform="translate(0 0.870117)"/>
                        </clipPath>
                    </defs>
                    </svg>
                </h2>
                <div className='flex items-center justify-center'>
                                 <svg width="100%" height="1px">
                                 <line 
                                     x1="0" y1="1" x2="100%" y2="1" 
                                     stroke="#f0f0f0" 
                                     strokeWidth="2" 
                                     strokeDasharray="10 4"
                                 />
                                 </svg>
                         </div>
                <div className="">
                    {event.faqs.map((faq, index) => (
                    <div key={index} className="overflow-hidden">
                        <button
                        onClick={() => toggleFAQ(index)}
                        className="w-full py-[20px] text-left flex items-center justify-between  transition-colors"
                        >
                        <span className="text-[18px] text-[#131313] font-normal tracking-[-0.9px]">
                            {faq.question}
                        </span>
                        <ChevronRight 
                            className={`w-[20px] h-[20px] text-[#000] transition-transform duration-200 ${
                            expandedFAQ === index ? 'rotate-90' : ''
                            }`} 
                        />
                        </button>
                                                 {expandedFAQ === index && (
                         <div className="pb-[16px]">
                             <p className="text-[14px] text-[#8c8c8c] leading-[1.2]">
                             {faq.answer}
                             </p>
                         </div>

                         )}
                         
                         {/* Only show dotted line if it's not the last FAQ */}
                         {index < event.faqs.length - 1 && (
                         <div className='flex items-center justify-center'>
                                 <svg width="100%" height="1px">
                                 <line 
                                     x1="0" y1="1" x2="100%" y2="1" 
                                     stroke="#f0f0f0" 
                                     strokeWidth="2" 
                                     strokeDasharray="10 4"
                                 />
                                 </svg>
                         </div>
                         )}
                    </div>
                    
                    ))}
                </div>
            </div>
         
        </div>
     
    </div>
    
    {/* Pricing + Book Tickets — mobile full-width bar; md+ floating card (matches property detail) */}
    <div className="fixed bottom-0 z-50 left-0 right-0 border-t border-[#dddddd] bg-white px-4 py-4 md:bottom-[40px] md:left-1/2 md:right-auto md:w-full md:max-w-[800px] md:-translate-x-1/2 md:rounded-[24px] md:border md:px-4 md:py-3 md:shadow-[0_8px_30px_rgba(0,0,0,0.08)]">
      <div className="mx-auto flex max-w-[800px] items-center justify-between gap-4">
        <div className="flex min-w-0 flex-col gap-0.5 md:flex-row md:items-center md:gap-2.5">
          <span className="text-[14px] text-black">Starting from</span>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[20px] font-bold text-black tabular-nums">
              ₹{event.pricing?.basePrice?.toLocaleString() || '0'}
            </span>
          </div>
        </div>
        <button
          type="button"
          data-book-sniff="true"
          disabled={!ctaInteractive}
          onClick={() => {
            if (razorpayEmbed?.url) {
              openRazorpayBooking(event);
              return;
            }
            if (contactToBook) {
              navigateEventContactBooking(navigate, event);
              return;
            }
            if (!eventBookingEnabled) return;
            const eventKey = event?.id || event?.eventId || id;
            if (typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches) {
              setTicketModalOpen(true);
            } else {
              navigate(`/ticket-selection/${eventKey}`, {
                state: {
                  event,
                  eventId: eventKey
                }
              });
            }
          }}
          className={`shrink-0 rounded-[14px] px-8 py-3.5 text-[16px] font-medium leading-none text-white transition-colors md:px-[60px] md:py-3.5 ${
            ctaInteractive
              ? 'bg-black hover:bg-gray-800'
              : 'cursor-default bg-[#8c8c8c] hover:bg-[#8c8c8c]'
          }`}
        >
          {eventTicketsCtaLabel(event)}
        </button>
      </div>
    </div>
    
      <PhotoGalleryModal
        open={showGallery}
        onClose={closeGallery}
        images={event.images}
        defaultPoster={eventGalleryPoster}
        venueTitle={[event.title, event.venue].filter(Boolean).join(' — ')}
        initialIndex={currentImage}
      />

      {eventBookingEnabled && ticketModalOpen ? (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8"
          role="dialog"
          aria-modal="true"
          aria-label="Select tickets"
        >
          <button
            type="button"
            className="absolute inset-0 bg-black/45"
            onClick={closeTicketModal}
            aria-label="Close ticket selection"
          />
          <div
            className="relative z-10 w-full max-w-[720px]"
            onClick={(e) => e.stopPropagation()}
          >
            <TicketSelectionPanel
              layout="modal"
              resolvedEventId={event?.id || event?.eventId || id}
              initialEvent={event}
              onRequestClose={closeTicketModal}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default EventDetail;
